import Fullscreen from 'components/AugmentedReality/Structure/Fullscreen'
import InstructionsFrame from 'components/AugmentedReality/Instructions'
import WelcomeFrame from 'components/AugmentedReality/Welcome'
import useContent from 'store/content'
import { useState, useEffect } from 'react'
import CalibrateFrame from 'components/AugmentedReality/Calibrate'
import ExperienceFrame from 'components/AugmentedReality/Experience'
import PhotoFrame from 'components/AugmentedReality/Photo'
import PhotoEditorFrame from 'components/AugmentedReality/PhotoEditor'
import useCamera from 'components/AugmentedReality/hooks/useCamera'
import { useLocalLanguage } from './hooks/useLocalLanguage'
import { I18nextProvider } from 'react-i18next'

interface AugmentedRealityProps {
  cardInfos: any;
  app: any;
  part: any;
  visitorId: string;
  isPwa: boolean;
}

const AugmentedReality = ({ part, isPwa = false  }: AugmentedRealityProps) => {
  const [step, setStep] = useState('welcome');
  const [hasAcceptedDownload, setHasAcceptedDownload] = useState(false);
  const [hasAcceptedCamera, setHasAcceptedCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string>('');
  const [currentOverlay, setCurrentOverlay] = useState<string>('');
  const camera = useCamera();
  const content = useContent()
  const augmentedReality = content.get.augmentedRealityById(part.id)
  const [visited, setVisited] = useState<boolean>(false)

  const defaultIndex = augmentedReality?.config.defaultFilter;
  const [currentFilter, setCurrentFilter] = useState<number>(defaultIndex !== undefined && defaultIndex !== -1 ? defaultIndex : 0);
  const [overlayTransform, setOverlayTransform] = useState<{ x: number; y: number; scale: number }>({ x: 0, y: 0, scale: 1 });

  useEffect(() => {
    if (!augmentedReality) return
    if (!isPwa) {
      const isVisited = localStorage.getItem(`${augmentedReality.id}-visited`)
      if (isVisited) {
        setVisited(true)
      }
    }
  }, [augmentedReality])

  const { isolatedI18n } = useLocalLanguage({ ra: augmentedReality });

  const handlePhotoTaken = (photo: string, overlay: string) => {
    setCapturedPhoto(photo);
    setCurrentOverlay(overlay);
    setStep('photoEditor');
  };

  if (!augmentedReality) { //in case RA was deleted
    return null;
  }

  return (
    <I18nextProvider i18n={isolatedI18n}>

      {step === 'welcome' && (
        <WelcomeFrame
          ra={augmentedReality}
          onNext={() => setStep('instructions')}
          visited={visited}
        ></WelcomeFrame>
      )}
      {step !== 'welcome' && (
        <Fullscreen>
          <>
            {step === 'instructions' && (
              <InstructionsFrame
                ra={augmentedReality}
                useCamera={hasAcceptedCamera}
                onUseCamera={setHasAcceptedCamera}
                onNext={() => setStep('calibrate')}
                onPrevious={() => {
                  if (camera) {
                    camera.stopCamera();
                  }
                  setStep('welcome')
                }}
                onCancel={() => setStep('welcome')}
              />
            )}
            {step === 'calibrate' && (
              <CalibrateFrame
                ra={augmentedReality}
                camera={camera}
                onNext={() => setStep('experience')}
                onPrevious={() => {
                  camera.stopCamera()
                  setStep('instructions')
                }}
                overlayTransform={overlayTransform}
                onOverlayTransformChange={setOverlayTransform}
              />
            )}
            {step === 'experience' && (
              <ExperienceFrame
                ra={augmentedReality}
                camera={camera}
                download={hasAcceptedDownload}
                onDownload={setHasAcceptedDownload}
                onPrevious={() => setStep('calibrate')}
                onCancel={() => setStep('welcome')}
                onPhoto={() => setStep('photo')}
                currentFilter={currentFilter}
                onFilterChange={setCurrentFilter}
                overlayTransform={overlayTransform}
                setVisited={setVisited}
                isPwa={isPwa}
              />
            )}
            {step === 'photo' && (
              <PhotoFrame
                ra={augmentedReality}
                camera={camera}
                onPrevious={() => setStep('experience')}
                onPhotoTaken={handlePhotoTaken}
                currentFilter={currentFilter}
                overlayTransform={overlayTransform}
              />
            )}
            {step === 'photoEditor' && (
              <PhotoEditorFrame
                ra={augmentedReality}
                camera={camera}
                download={hasAcceptedDownload}
                onDownload={setHasAcceptedDownload}
                onPrevious={() => setStep('photo')}
                onCancel={() => setStep('welcome')}
                onGoLive={async () => {
                  const mediaStream = await camera.startCamera()
                  if (mediaStream) {
                    setStep('experience')
                  }
                }}
                capturedPhoto={capturedPhoto}
                currentOverlay={currentOverlay}
                overlayTransform={overlayTransform}
              />
            )}
          </>
        </Fullscreen>
      )}
    </I18nextProvider>

  )
}

export default AugmentedReality;